import {jwtDecode} from 'jwt-decode';
import {CustomerToken} from '__generated__/__types__';
import {WebStorage} from '@/_application/core/storage/web-storage';
import {AuthEnum} from '@/_application/modules/auth/enum/auth';
import {refreshToken} from '@/framework/customer.ssr';
import {logSentry} from '@/lib/sentry';
import {getSession, signOut} from 'next-auth/react';


let tokenPromise: Promise<string | null> | null = null;

/**
 * Check if the access token is expired
 * @param {string} token - The access token
 * @returns {boolean} - True if the token is expired, false otherwise
 */
function isAccessTokenExpired(token: string): boolean {
    try {
        const decodedToken: {exp: number} = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);
        return decodedToken.exp < currentTime;
    } catch (error) {
        console.error('Failed to decode token', error);
        return true;
    }
}

/**
 * Get the customer token and refresh it if it's expired
 * @returns {Promise<CustomerToken | null>} - The token if it exists and is not expired, null otherwise
 */
export async function getCustomerToken(): Promise<CustomerToken | null> {
    const tokenString = WebStorage.getItem(AuthEnum.TOKEN) ?? null;
    if (tokenString === null) {
        return null;
    }

    const token = JSON.parse(tokenString) as CustomerToken;

    if (isAccessTokenExpired(token.access_token ?? '')) {
        try {
            // Refresh the token when it's expired
            const result = await refreshToken(token.refresh_token ?? '');
            token.access_token = result.access_token;
            WebStorage.setItem(AuthEnum.TOKEN, JSON.stringify(token));
        } catch (error) {
            logSentry({
                name: 'Failed to refresh token',
                transactionName: 'getCustomerToken',
                error: error as any,
                extra: {},
            });
            console.error('Failed to refresh token', error);
            WebStorage.clearAll();
            return null;
        }
    }

    return token;
}

let isResolvingToken = false;

/**
 * Get the customer access token with a queuing mechanism
 * to avoid race conditions when multiple requests are made.
 * @returns {Promise<string | null>} - Returns the access token or null
 */
export async function getCustomerAccessToken(): Promise<string | null> {
    if (isResolvingToken) {
        return tokenPromise;
    }

    isResolvingToken = true;
    tokenPromise = (async () => {
        try {
            return WebStorage.getItem(AuthEnum.ACCESS_TOKEN) ?? null;
        } catch (error) {
            console.error('Error accessing token', error);
            return null;
        } finally {
            isResolvingToken = false;
            tokenPromise = null;
        }
    })();

    return tokenPromise;
}

export async function getCustomerRefreshToken(): Promise<string | null> {
    const session = await getSession();

    return (
        session?.token &&
        // @ts-ignore: refresh_token exists at runtime
        session.token.refresh_token
    ) ?? null;
}

/**
 * Generate a cache ID using SHA256
 * @param {string} storeId - The store ID
 * @param {string} currency - The currency
 * @param {boolean} isLoggedIn - Is user logged in
 * @param {string} customerGroup - The customer group
 * @param {number} customerTaxRate - The customer tax rate
 * @param {string} salt - Additional salt value
 * @returns {Promise<string>} - A SHA256 hash
 */
/**
 * Pure JS deterministic hash fallback for browser in non-secure context (HTTP).
 * Not cryptographic, but sufficient for cache key generation.
 */
function fallbackHash(str: string): string {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

    return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}

/**
 * Cached reference to Node.js crypto.createHash.
 * Resolved once on first server-side call, then reused — eliminates
 * the ~5-15ms dynamic import('crypto') overhead on EVERY request.
 */
let _serverCreateHash: typeof import('crypto').createHash | null = null;

async function getServerCreateHash(): Promise<typeof import('crypto').createHash> {
    if (!_serverCreateHash) {
        const crypto = await import('crypto');
        _serverCreateHash = crypto.createHash;
    }
    return _serverCreateHash;
}

export async function generateCacheId(
    storeId: string,
    currency: string,
    isLoggedIn: boolean,
    customerGroup: string,
    customerTaxRate: number,
    salt: string,
): Promise<string> {
    const data = storeId + currency + isLoggedIn + customerGroup + customerTaxRate + salt;

    // Server-side: use Node.js crypto (cached after first call — no dynamic import overhead)
    if (typeof window === 'undefined') {
        const createHash = await getServerCreateHash();
        return createHash('sha256').update(data).digest('hex');
    }

    // Browser with secure context (HTTPS): use Web Crypto API
    if (window.crypto?.subtle) {
        const encoder = new TextEncoder();
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoder.encode(data));
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    // Browser without secure context (HTTP / localhost): pure JS fallback
    return fallbackHash(data);
}

export const setCustomerToken = (token: CustomerToken) => {
    if (token?.access_token) {
        WebStorage.setItem(AuthEnum.ACCESS_TOKEN, token?.access_token);
    }
};

export const clearAuthData = async () => {
    WebStorage.clearAll();
    tokenPromise = null;
    // Clear the session in next-auth
    await signOut({
        redirect: false,
    });
};
